/**
 * The Golf Printer — Bespoke Theme JavaScript
 * Includes full multi-image gallery interaction, thumbnail switching,
 * variant selector handling, quantity adjusters, instant direct checkout,
 * smooth navigation, and contact form handling.
 */
document.addEventListener('DOMContentLoaded', () => {
  // 1. Initialize Product Media Gallery
  initProductGallery();

  // 2. Initialize Product Variant Selector
  initVariantSelector();

  // 3. Initialize Quantity Controls (Product & Cart)
  initQuantityControls();

  // 4. Initialize Buy It Now Instant Direct Checkout
  initBuyItNow();

  // 5. Handle Commission Form Submission
  initContactForm();

  // 6. Smooth Anchor Scrolling
  initSmoothScroll();
});

/**
 * Multi-Image Gallery Manager
 */
function initProductGallery() {
  const gallery = document.getElementById('product-gallery');
  if (!gallery) return;

  const slides = gallery.querySelectorAll('.product-media-slide');
  const thumbs = gallery.querySelectorAll('.product-thumb-item');
  const prevBtn = document.getElementById('gallery-prev-btn');
  const nextBtn = document.getElementById('gallery-next-btn');
  const counter = document.getElementById('gallery-counter');
  
  if (slides.length === 0) return;

  let currentIndex = 0;

  function setActiveSlide(index) {
    if (index < 0) index = slides.length - 1;
    if (index >= slides.length) index = 0;
    currentIndex = index;

    // Update slides
    slides.forEach((slide, i) => {
      if (i === currentIndex) {
        slide.classList.add('is-active');
      } else {
        slide.classList.remove('is-active');
      }
    });

    // Update thumbnails
    thumbs.forEach((thumb, i) => {
      if (i === currentIndex) {
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-selected', 'true');
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      } else {
        thumb.classList.remove('is-active');
        thumb.setAttribute('aria-selected', 'false');
      }
    });

    // Update counter
    if (counter) {
      counter.textContent = `${currentIndex + 1} / ${slides.length}`;
    }
  }

  // Thumbnail clicks
  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const targetIndex = parseInt(thumb.getAttribute('data-target-index'), 10);
      if (!isNaN(targetIndex)) {
        setActiveSlide(targetIndex);
      }
    });
  });

  // Prev / Next button clicks
  if (prevBtn) {
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      setActiveSlide(currentIndex - 1);
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      setActiveSlide(currentIndex + 1);
    });
  }

  // Keyboard navigation when gallery has focus
  window.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') {
      setActiveSlide(currentIndex - 1);
    } else if (e.key === 'ArrowRight') {
      setActiveSlide(currentIndex + 1);
    }
  });

  // Touch Swipe support
  let touchStartX = 0;
  let touchEndX = 0;
  const mainImageFrame = document.getElementById('main-image-container');

  if (mainImageFrame) {
    mainImageFrame.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    mainImageFrame.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    }, { passive: true });
  }

  function handleSwipe() {
    const swipeThreshold = 50;
    if (touchEndX < touchStartX - swipeThreshold) {
      setActiveSlide(currentIndex + 1); // Swipe left -> next image
    }
    if (touchEndX > touchStartX + swipeThreshold) {
      setActiveSlide(currentIndex - 1); // Swipe right -> prev image
    }
  }

  // Global helper to switch to a specific media ID (called by variant changes)
  window.switchProductGalleryToMediaId = function(mediaId) {
    if (!mediaId) return;
    slides.forEach((slide, i) => {
      if (slide.getAttribute('data-media-id') == mediaId) {
        setActiveSlide(i);
      }
    });
  };
}

/**
 * Product Variant Selector
 */
function initVariantSelector() {
  const variantRadios = document.querySelectorAll('input[name="variant-radio"]');
  const hiddenVariantInput = document.getElementById('selected-variant-id');
  const priceDisplay = document.getElementById('product-price-display');
  const buyItNowBtn = document.getElementById('BuyItNowBtn');

  if (variantRadios.length === 0) return;

  variantRadios.forEach((radio) => {
    radio.addEventListener('change', () => {
      if (radio.checked) {
        const variantId = radio.value;
        const variantPrice = radio.getAttribute('data-price');
        const mediaId = radio.getAttribute('data-media-id');

        if (hiddenVariantInput) {
          hiddenVariantInput.value = variantId;
        }

        if (buyItNowBtn) {
          buyItNowBtn.setAttribute('data-variant-id', variantId);
        }

        if (priceDisplay && variantPrice) {
          priceDisplay.textContent = variantPrice;
        }

        // Highlight selected pill
        document.querySelectorAll('.variant-pill-option').forEach(pill => {
          pill.classList.remove('is-selected');
        });
        radio.closest('.variant-pill-option')?.classList.add('is-selected');

        // Switch gallery to variant's image if available
        if (mediaId && window.switchProductGalleryToMediaId) {
          window.switchProductGalleryToMediaId(mediaId);
        }
      }
    });
  });
}

/**
 * Quantity Plus/Minus Adjusters for Product and Cart Pages
 */
function initQuantityControls() {
  // 1. Product Page Quantity Controls
  document.querySelectorAll('.prod-qty-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const input = btn.closest('.product-qty-control')?.querySelector('.quantity-input');
      if (!input) return;
      let val = parseInt(input.value, 10) || 1;
      if (btn.classList.contains('qty-plus')) {
        val += 1;
      } else if (btn.classList.contains('qty-minus')) {
        val = Math.max(1, val - 1);
      }
      input.value = val;
    });
  });

  // 2. Cart Page Quantity Controls
  document.querySelectorAll('.cart-qty-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const input = btn.closest('.cart-qty-control')?.querySelector('.cart-qty-input');
      if (!input) return;
      let val = parseInt(input.value, 10) || 0;
      if (btn.classList.contains('qty-plus')) {
        val += 1;
      } else if (btn.classList.contains('qty-minus')) {
        val = Math.max(0, val - 1);
      }
      input.value = val;
      
      // Submit cart update
      const cartForm = document.getElementById('cart-form');
      if (cartForm) {
        cartForm.submit();
      }
    });
  });
}

/**
 * Buy It Now — 1-Click Instant Direct Checkout
 */
function initBuyItNow() {
  const buyItNowBtn = document.getElementById('BuyItNowBtn');
  if (!buyItNowBtn) return;

  buyItNowBtn.addEventListener('click', async (e) => {
    e.preventDefault();
    const variantId = buyItNowBtn.getAttribute('data-variant-id') || document.getElementById('selected-variant-id')?.value;
    const qtyInput = document.getElementById('Quantity');
    const quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

    if (!variantId) {
      window.location.href = '/checkout';
      return;
    }

    const originalText = buyItNowBtn.innerHTML;
    buyItNowBtn.innerHTML = '<span>Processing...</span>';
    buyItNowBtn.disabled = true;

    try {
      // Add variant to cart via Shopify Cart API
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (res.ok) {
        // Redirect straight to Shopify Checkout
        window.location.href = '/checkout';
      } else {
        // Fallback to standard product form submit
        document.getElementById('product-form')?.submit();
      }
    } catch (err) {
      console.error('Buy It Now redirect error:', err);
      // Fallback: direct navigation to checkout or form submission
      document.getElementById('product-form')?.submit();
    }
  });
}

/**
 * Contact & Commission Form
 */
function initContactForm() {
  const contactForm = document.getElementById('commission-inquiry-form');
  if (!contactForm) return;

  contactForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const clubName = document.getElementById('inquiry-club-name')?.value || 'Golf Club';
    const senderName = document.getElementById('inquiry-sender-name')?.value || '';
    const senderEmail = document.getElementById('inquiry-sender-email')?.value || '';
    const message = document.getElementById('inquiry-message')?.value || '';

    const subject = encodeURIComponent(`Poster Commission Inquiry: ${clubName}`);
    const body = encodeURIComponent(
      `Hello The Golf Printer Team,\n\n` +
      `I would like to inquire about commissioning custom championship golf posters for our club.\n\n` +
      `Club Name: ${clubName}\n` +
      `Contact Name: ${senderName}\n` +
      `Contact Email: ${senderEmail}\n\n` +
      `Project Details:\n${message}\n\n` +
      `Best regards,\n${senderName}`
    );

    window.location.href = `mailto:Andrew@TheGolfPrinter.com?subject=${subject}&body=${body}`;
  });
}

/**
 * Smooth Scrolling
 */
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const targetId = this.getAttribute('href');
      if (targetId && targetId !== '#') {
        const targetElement = document.querySelector(targetId);
        if (targetElement) {
          e.preventDefault();
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    });
  });
}

